/**
 * EventWorkspacePage (Web) — Unified single-page view for one event.
 *
 * Layout mirrors the desktop EventWorkspace:
 * - Header: editable title, date, status, Generate CTA
 * - Left sidebar: lineup, venue, tasks
 * - Main area: tabs (Flyers, Generate, Details, Guest List)
 *
 * Uses Convex queries/mutations for all data operations.
 */

import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAuth } from '@workos-inc/authkit-react';
import { InlineEntityInput } from '../components/InlineEntityInput';
import type { Id } from '../../convex/_generated/dataModel';
import {
  ArrowLeft,
  CalendarDays,
  MapPin,
  Users,
  Image as ImageIcon,
  Sparkles,
  FileText,
  UserPlus,
  Plus,
  Check,
  X,
  Loader2,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  ListChecks,
  LogOut,
  LayoutGrid,
  BarChart2,
} from 'lucide-react';

type Tab = 'flyers' | 'generate' | 'details' | 'guests';

export function EventWorkspacePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const event = useQuery(
    api.events.get,
    eventId ? { id: eventId as Id<'entities'> } : 'skip',
  );
  const entities = useQuery(api.entities.list, {});
  const tasks = useQuery(
    api.events.getTasks,
    eventId ? { eventId: eventId as Id<'entities'> } : 'skip',
  );
  const guests = useQuery(
    api.events.getGuests,
    eventId ? { eventId: eventId as Id<'entities'> } : 'skip',
  );

  const updateEvent = useMutation(api.events.update);
  const addTaskMut = useMutation(api.events.addTask);
  const toggleTaskMut = useMutation(api.events.toggleTask);
  const removeTaskMut = useMutation(api.events.removeTask);
  const addGuestMut = useMutation(api.events.addGuest);
  const removeGuestMut = useMutation(api.events.removeGuest);

  const [activeTab, setActiveTab] = useState<Tab>('details');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [showTasks, setShowTasks] = useState(true);

  // Guest form state
  const [newGuestName, setNewGuestName] = useState('');
  const [newGuestStatus, setNewGuestStatus] = useState<string>('paid');

  if (event === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (event === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground">Event not found</p>
          <button
            onClick={() => navigate('/app/dashboard')}
            className="text-sm text-[#25a48f] hover:underline"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const meta = (event.metadata ?? {}) as Record<string, unknown>;
  const lineup = (meta.lineup as string[]) ?? [];
  const venueId = meta.venueId as string | null;
  const status = (meta.status as string) ?? 'confirmed';
  const dateStr = meta.date as string | null;
  const eventDate = dateStr ? new Date(dateStr) : null;
  const notes = (meta.notes as string) ?? '';
  const tags = (meta.tags as string[]) ?? [];
  const visibility = (meta.visibility as string) ?? 'private';

  // Derive venue name
  const venue = entities?.find((e) => e._id === venueId);

  // Derive lineup entities
  const lineupEntities = lineup
    .map((id) => entities?.find((e) => e._id === id))
    .filter(Boolean) as Array<{ _id: string; name: string; entityType: string }>;

  const taskList = tasks ?? [];
  const completedTasks = taskList.filter((t: any) => t.status === 'done').length;
  const guestList = guests ?? [];

  // Title editing
  const startEditTitle = () => {
    setEditTitle(event.name);
    setIsEditingTitle(true);
  };

  const saveTitle = async () => {
    if (!editTitle.trim() || editTitle.trim() === event.name) {
      setIsEditingTitle(false);
      return;
    }
    await updateEvent({ id: event._id, title: editTitle.trim() });
    setIsEditingTitle(false);
  };

  // Lineup management
  const addToLineup = async (entity: { _id: string; name: string; entityType: string }) => {
    if (lineup.includes(entity._id)) return;
    await updateEvent({ id: event._id, lineup: [...lineup, entity._id] });
  };

  const removeFromLineup = async (entityId: string) => {
    await updateEvent({
      id: event._id,
      lineup: lineup.filter((id) => id !== entityId),
    });
  };

  // Task management
  const addTask = async () => {
    if (!newTaskTitle.trim()) return;
    const taskId = crypto.randomUUID();
    await addTaskMut({
      eventId: event._id,
      taskId,
      title: newTaskTitle.trim(),
    });
    setNewTaskTitle('');
  };

  const toggleTask = async (taskId: string) => {
    await toggleTaskMut({ eventId: event._id, taskId });
  };

  const removeTask = async (taskId: string) => {
    await removeTaskMut({ eventId: event._id, taskId });
  };

  // Guest management
  const addGuest = async () => {
    if (!newGuestName.trim()) return;
    const guestId = crypto.randomUUID();
    await addGuestMut({
      eventId: event._id,
      guestId,
      name: newGuestName.trim(),
      status: newGuestStatus,
      plusOnes: 0,
    });
    setNewGuestName('');
  };

  const removeGuest = async (guestId: string) => {
    await removeGuestMut({ eventId: event._id, guestId });
  };

  const statusColors: Record<string, string> = {
    comp: 'bg-[#25a48f]/10 text-[#25a48f]',
    paid: 'bg-blue-500/10 text-blue-500',
    vip: 'bg-amber-500/10 text-amber-500',
    plus_one: 'bg-purple-500/10 text-purple-500',
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ─── Top Nav ─── */}
      <header className="border-b border-border/50 sticky top-0 bg-background/95 backdrop-blur-sm z-10">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/eventtools-logo.svg" alt="Event.Tools" className="h-7 w-auto" />
            <span className="font-semibold text-foreground">Event.Tools</span>
          </Link>

          <nav className="flex items-center gap-1">
            <Link
              to="/app/dashboard"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <LayoutGrid className="w-4 h-4" />
              Dashboard
            </Link>
            <Link
              to="/app/editor"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Editor
            </Link>
            <Link
              to="/app/insights"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <BarChart2 className="w-4 h-4" />
              Insights
            </Link>
          </nav>

          <button
            onClick={() => void signOut()}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-sm"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* ─── Event Header ─── */}
      <div className="flex-shrink-0 border-b border-border bg-card px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/app/dashboard')}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Dashboard
          </button>

          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {isEditingTitle ? (
                <div className="flex items-center gap-2 mb-1">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveTitle();
                      if (e.key === 'Escape') setIsEditingTitle(false);
                    }}
                    autoFocus
                    className="text-xl font-bold bg-transparent border-b-2 border-[#25a48f] focus:outline-none w-full"
                  />
                  <button onClick={saveTitle} className="p-1 rounded hover:bg-muted">
                    <Check className="w-4 h-4 text-[#25a48f]" />
                  </button>
                  <button onClick={() => setIsEditingTitle(false)} className="p-1 rounded hover:bg-muted">
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              ) : (
                <h1
                  className="text-xl font-bold cursor-pointer hover:text-[#25a48f] transition-colors group flex items-center gap-2 mb-1"
                  onClick={startEditTitle}
                >
                  {event.name}
                  <Pencil className="w-3.5 h-3.5 opacity-0 group-hover:opacity-50 transition-opacity" />
                </h1>
              )}

              <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                {eventDate && (
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" />
                    <span>
                      {eventDate.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })}{' '}
                      at{' '}
                      {eventDate.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                )}
                {venue && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{venue.name}</span>
                  </div>
                )}
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    status === 'confirmed'
                      ? 'bg-[#25a48f]/10 text-[#25a48f]'
                      : status === 'tentative'
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'bg-red-500/10 text-red-500'
                  }`}
                >
                  {status}
                </span>
              </div>
            </div>

            <button
              onClick={() => navigate('/app/editor')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#ef5228] text-white hover:bg-[#d9461f] transition-colors text-sm font-medium flex-shrink-0"
            >
              <Sparkles className="w-4 h-4" />
              Generate Flyer
            </button>
          </div>
        </div>
      </div>

      {/* ─── Body: Sidebar + Main ─── */}
      <div className="flex-1 flex min-h-0">
        {/* ─── Left Sidebar ─── */}
        <aside className="w-[280px] flex-shrink-0 border-r border-border bg-card overflow-y-auto hidden md:block">
          <div className="p-4 space-y-5">
            {/* Lineup */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                Lineup ({lineupEntities.length})
              </h3>
              <div className="space-y-1 mb-2">
                {lineupEntities.map((item) => (
                  <div
                    key={item._id}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/50 group"
                  >
                    <span className="text-sm truncate">{item.name}</span>
                    <button
                      onClick={() => removeFromLineup(item._id)}
                      className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-all"
                    >
                      <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                ))}
                {lineupEntities.length === 0 && (
                  <p className="text-xs text-muted-foreground italic px-2">No artists yet</p>
                )}
              </div>
              <InlineEntityInput
                defaultType="dj"
                placeholder="Add artist..."
                onSelect={addToLineup}
                excludeIds={lineup}
                filterTypes={[
                  'dj', 'band', 'singer', 'mc', 'performer',
                  'gogo_dancer', 'vj', 'lighting_designer',
                  'performance_artist', 'dancer',
                ]}
                compact
              />
            </section>

            {/* Venue */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                Venue
              </h3>
              {venue ? (
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-muted/30">
                  <span className="text-sm">{venue.name}</span>
                </div>
              ) : (
                <InlineEntityInput
                  defaultType="venue"
                  placeholder="Set venue..."
                  onSelect={async (entity) => {
                    await updateEvent({ id: event._id, venueId: entity._id });
                  }}
                  filterTypes={['venue']}
                  compact
                />
              )}
            </section>

            {/* Tasks */}
            <section>
              <button
                onClick={() => setShowTasks(!showTasks)}
                className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2"
              >
                <span className="flex items-center gap-1.5">
                  <ListChecks className="w-3.5 h-3.5" />
                  Tasks ({completedTasks}/{taskList.length})
                </span>
                {showTasks ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              {showTasks && (
                <div className="space-y-1">
                  {taskList.map((task: any) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-muted/50 group"
                    >
                      <button
                        onClick={() => toggleTask(task.id)}
                        className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                          task.status === 'done'
                            ? 'bg-[#25a48f] border-[#25a48f]'
                            : 'border-input hover:border-[#25a48f]'
                        }`}
                      >
                        {task.status === 'done' && <Check className="w-2.5 h-2.5 text-white" />}
                      </button>
                      <span
                        className={`text-xs flex-1 truncate ${
                          task.status === 'done' ? 'line-through text-muted-foreground' : ''
                        }`}
                      >
                        {task.title}
                      </span>
                      <button
                        onClick={() => removeTask(task.id)}
                        className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-muted"
                      >
                        <X className="w-2.5 h-2.5 text-muted-foreground" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addTask()}
                      placeholder="Add task..."
                      className="flex-1 text-xs bg-transparent border border-input rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground/50"
                    />
                    {newTaskTitle.trim() && (
                      <button
                        onClick={addTask}
                        className="p-1 rounded bg-[#25a48f]/10 hover:bg-[#25a48f]/20 text-[#25a48f]"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>
        </aside>

        {/* ─── Main Content ─── */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Tabs */}
          <div className="flex-shrink-0 border-b border-border px-6">
            <nav className="flex gap-1 -mb-px">
              {([
                { id: 'details' as const, label: 'Details', icon: FileText },
                { id: 'guests' as const, label: 'Guest List', icon: UserPlus },
                { id: 'generate' as const, label: 'Generate', icon: Sparkles },
              ]).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-[#25a48f] text-[#25a48f]'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'details' && (
              <div className="max-w-2xl space-y-6">
                <h2 className="text-lg font-semibold">Event Details</h2>
                <div className="grid grid-cols-2 gap-4">
                  <DetailField label="Status" value={status} />
                  <DetailField label="Visibility" value={visibility} />
                  {eventDate && (
                    <>
                      <DetailField
                        label="Date"
                        value={eventDate.toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      />
                      <DetailField
                        label="Time"
                        value={eventDate.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      />
                    </>
                  )}
                  <DetailField label="Venue" value={venue?.name ?? 'Not set'} />
                  <DetailField label="Artists" value={`${lineup.length} in lineup`} />
                </div>
                {notes && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Notes</label>
                    <p className="text-sm bg-muted/30 rounded-lg p-3 whitespace-pre-wrap">{notes}</p>
                  </div>
                )}
                {tags.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Tags</label>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2 py-0.5 rounded-full bg-muted border border-border"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'guests' && (
              <div className="max-w-2xl space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Guest List ({guestList.length})</h2>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newGuestName}
                    onChange={(e) => setNewGuestName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addGuest()}
                    placeholder="Guest name..."
                    className="flex-1 text-sm px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                  <select
                    value={newGuestStatus}
                    onChange={(e) => setNewGuestStatus(e.target.value)}
                    className="text-xs px-2 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="paid">Paid</option>
                    <option value="comp">Comp</option>
                    <option value="vip">VIP</option>
                    <option value="plus_one">+1</option>
                  </select>
                  <button
                    onClick={addGuest}
                    disabled={!newGuestName.trim()}
                    className="px-3 py-2 rounded-lg bg-[#25a48f] text-white text-sm font-medium hover:bg-[#1e9480] transition-colors disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>

                {guestList.length > 0 ? (
                  <div className="rounded-xl border border-border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/50 text-xs text-muted-foreground">
                          <th className="text-left px-4 py-2 font-medium">Name</th>
                          <th className="text-left px-4 py-2 font-medium">Status</th>
                          <th className="text-left px-4 py-2 font-medium">+1s</th>
                          <th className="w-10" />
                        </tr>
                      </thead>
                      <tbody>
                        {guestList.map((g: any) => (
                          <tr key={g.id} className="border-t border-border hover:bg-muted/30 group">
                            <td className="px-4 py-2 font-medium">{g.name}</td>
                            <td className="px-4 py-2">
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  statusColors[g.status] ?? ''
                                }`}
                              >
                                {g.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-muted-foreground">{g.plusOnes || '\u2014'}</td>
                            <td className="px-2 py-2">
                              <button
                                onClick={() => removeGuest(g.id)}
                                className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-muted"
                              >
                                <Trash2 className="w-3 h-3 text-muted-foreground" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No guests yet. Add someone above to get started.
                  </p>
                )}
              </div>
            )}

            {activeTab === 'generate' && (
              <div className="max-w-2xl mx-auto text-center py-12 space-y-6">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-[#ef5228]/10 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-[#ef5228]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold mb-2">AI Flyer Generation</h2>
                  <p className="text-muted-foreground text-sm max-w-md mx-auto">
                    Generate a professional flyer for &ldquo;{event.name}&rdquo; using AI.
                    Your event details, lineup, and venue will be used as context automatically.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/app/editor')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#ef5228] text-white font-medium hover:bg-[#d9461f] transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  Open Editor
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground block mb-0.5">{label}</label>
      <p className="text-sm font-medium capitalize">{value}</p>
    </div>
  );
}
